// client/src/dnd/views/Interactive_parts/ShapeEditToolbar.js
import React, { useState, useEffect, useRef } from 'react';
import { getShapeType } from '../../components/pathing_tools/PathingManager';
import IconUpload from '../../components/IconUpload';

const RECENT_LINE_COLOURS_KEY = 'point_atlas_recent_line_colours';
const RECENT_FILL_COLOURS_KEY = 'point_atlas_recent_fill_colours';
const API_BASE = '/api/polygons';

const REVERB_CHARACTERS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

const SPEECH_EFFECT_PRESETS = [
  {
    value: 'small_room',
    label: 'Small Room',
    props: {
      reverbAmount: 1.2,
      reverbCharacter: 'small',
      compressionAmount: 2.0,
      brightnessAmount: 5.4,
    },
  },
  {
    value: 'large_room',
    label: 'Large Room',
    props: {
      reverbAmount: 2.6,
      reverbCharacter: 'medium',
      compressionAmount: 2.2,
      brightnessAmount: 5.1,
    },
  },
  {
    value: 'sewers',
    label: 'Sewers',
    props: {
      reverbAmount: 3.9,
      reverbCharacter: 'medium',
      compressionAmount: 3.3,
      brightnessAmount: 4.0,
    },
  },
  {
    value: 'cave',
    label: 'Cave',
    props: {
      reverbAmount: 5.1,
      reverbCharacter: 'large',
      compressionAmount: 2.8,
      brightnessAmount: 3.8,
    },
  },
  {
    value: 'cathedral',
    label: 'Cathedral',
    props: {
      reverbAmount: 7.2,
      reverbCharacter: 'large',
      compressionAmount: 1.6,
      brightnessAmount: 6.2,
    },
  },
  {
    value: 'tomb',
    label: 'Tomb',
    props: {
      reverbAmount: 4.6,
      reverbCharacter: 'large',
      compressionAmount: 2.5,
      brightnessAmount: 3.2,
    },
  },
];

function getRecentColours(key) {
  try {
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    if (Array.isArray(arr)) return arr;
  } catch (e) {}
  return [];
}

function addRecentColour(key, hex) {
  try {
    let arr = getRecentColours(key);
    arr = arr.filter(x => x !== hex);
    arr.unshift(hex);
    arr = arr.slice(0, 5);
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (e) {}
}

const toolbarStyle = {
  position: 'absolute',
  top: 16,
  left: 70,
  display: 'flex',
  alignItems: 'center',
  background: '#191a1f',
  borderRadius: '12px',
  boxShadow: '0 2px 12px #000b',
  padding: '6px 14px 6px 14px',
  zIndex: 2500,
  gap: 12,
  height: 40,
  minWidth: 420,
};

function ColourPickerModal({
  value,
  recent,
  onChange,
  onCancel,
  onConfirm,
  title = 'Pick Colour'
}) {
  const [internalValue, setInternalValue] = useState(value);
  const originalValue = useRef(value);

  useEffect(() => {
    setInternalValue(value);
    originalValue.current = value;
  }, [value]);

  function handleCancel() {
    setInternalValue(originalValue.current);
    onChange(originalValue.current);
    onCancel();
  }

  function handleConfirm() {
    onConfirm(internalValue);
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(20,20,22,0.45)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#23242a',
        padding: 32,
        borderRadius: 13,
        boxShadow: '0 2px 20px #000a',
        minWidth: 320,
        display: 'flex', flexDirection: 'column', gap: 18,
        alignItems: 'stretch'
      }}>
        <div style={{ color: '#ffe670', fontWeight: 700, fontSize: 19 }}>
          {title}
        </div>
        <input
          type="color"
          value={internalValue}
          onChange={e => {
            setInternalValue(e.target.value);
            onChange(e.target.value);
          }}
          style={{
            width: 90,
            height: 48,
            border: 'none',
            borderRadius: 12,
            alignSelf: 'center',
            background: 'none',
            cursor: 'pointer',
            boxShadow: '0 0 0 2px #444'
          }}
        />
        {recent.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 6 }}>
            {recent.map(hex => (
              <span
                key={hex}
                onClick={() => {
                  setInternalValue(hex);
                  onChange(hex);
                }}
                style={{
                  width: 28,
                  height: 28,
                  background: hex,
                  borderRadius: 8,
                  border: hex === internalValue ? '3px solid #ffe670' : '1px solid #444',
                  display: 'inline-block',
                  cursor: 'pointer',
                  boxShadow: hex === '#fff' ? '0 0 0 1px #666' : ''
                }}
                title={hex}
              />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 14, marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={handleCancel}
            style={{
              background: '#31313c',
              color: '#eee',
              border: 'none',
              borderRadius: 7,
              padding: '8px 23px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer'
            }}
          >Cancel</button>
          <button
            onClick={handleConfirm}
            style={{
              background: '#ffe670',
              color: '#23242a',
              border: 'none',
              borderRadius: 7,
              padding: '8px 23px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer'
            }}
          >Confirm</button>
        </div>
      </div>
    </div>
  );
}

function tryParseJSON(s, fallback) {
  try {
    return typeof s === 'string' ? JSON.parse(s) : s ?? fallback;
  } catch {
    return fallback;
  }
}

function toSnakeCaseShape(shape) {
  if (!shape) return shape;
  return {
    id: shape.id,
    map_id: shape.mapId || shape.map_id,
    label: shape.label,
    geometry: shape.geometry,
    fill_color: shape.fillColor,
    fill_opacity: shape.fillOpacity,
    stroke_color: shape.strokeColor,
    stroke_opacity: shape.strokeOpacity,
    radius: shape.radius,
    pattern: shape.pattern,
    shape_type: shape.shapeType,
    behavior_type: shape.behaviorType ?? null,
    behavior_props: shape.behaviorProps ? JSON.stringify(shape.behaviorProps) : '{}',
    behavior_state: shape.behaviorState ? JSON.stringify(shape.behaviorState) : '{}',
    sprite: shape.sprite ?? null,
    token_default_position: shape.tokenDefaultPosition,
    token_speed: shape.tokenSpeed,
    token_auto_move: shape.tokenAutoMove,
    token_direction: shape.tokenDirection,
  };
}

function serializeForBackend(shape) {
  const out = {
    mapId: shape.mapId || shape.map_id,
    userId: shape.userId || shape.user_id || null,
    label: shape.label || '',
    geometry: shape.geometry,
    fillColor: shape.fillColor ?? '#ff0000',
    strokeColor: shape.strokeColor ?? '#ff0000',
    strokeOpacity: shape.strokeOpacity ?? 1.0,
    fillOpacity: shape.fillOpacity ?? 0.5,
    pattern: shape.pattern || '',
    radius: shape.radius ?? null,
    shapeType: shape.shapeType || shape.shape_type || '',
    behaviorType: shape.behaviorType ?? shape.behavior_type ?? null,
    behaviorProps: shape.behaviorProps ?? (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {}),
    behaviorState: shape.behaviorState ?? (shape.behavior_state ? tryParseJSON(shape.behavior_state, {}) : {}),
    sprite: shape.sprite ?? null,
    tokenDefaultPosition: typeof shape.tokenDefaultPosition === 'number'
      ? shape.tokenDefaultPosition
      : undefined,
    tokenSpeed: typeof shape.tokenSpeed === 'number'
      ? shape.tokenSpeed
      : undefined,
    tokenAutoMove: typeof shape.tokenAutoMove === 'boolean'
      ? shape.tokenAutoMove
      : undefined,
    tokenDirection: shape.tokenDirection || undefined,
  };
  return out;
}

function Panel({ children, label }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      background: '#24253a',
      border: '1px solid #393b49',
      borderRadius: 7,
      padding: '5px 9px'
    }}>
      <span style={{ color: '#fff', fontSize: 13, marginRight: 6 }}>{label}</span>
      {children}
    </div>
  );
}

function getDefaultSpeechBehaviorProps(existing = {}) {
  return {
    reverbAmount: typeof existing.reverbAmount === 'number' ? Math.max(0, Math.min(10, existing.reverbAmount)) : 1,
    reverbCharacter: typeof existing.reverbCharacter === 'string' && existing.reverbCharacter ? existing.reverbCharacter : 'medium',
    compressionAmount: typeof existing.compressionAmount === 'number' ? Math.max(0, Math.min(10, existing.compressionAmount)) : 0,
    brightnessAmount: typeof existing.brightnessAmount === 'number' ? Math.max(0, Math.min(10, existing.brightnessAmount)) : 5,
  };
}

function dispatchPolygonsChanged(mapId, shapeId = null) {
  try {
    window.dispatchEvent(
      new CustomEvent('pa-polygons-changed', {
        detail: {
          mapId: mapId ?? null,
          shapeId: shapeId ?? null,
          at: Date.now(),
        },
      })
    );
  } catch {}
}

export default function ShapeEditToolbar({
  shape,
  layer,
  onChange,
  onSave,
  onClose,
  onDelete,
  saving = false,
  canEdit = false,
  convertToSnakeCase = false,
  extraRegionMaps = [],
  parentMapId = null,
  parentMapName = 'Parent Map',
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [colourModal, setColourModal] = useState(null);
  const [recentLineColours, setRecentLineColours] = useState(getRecentColours(RECENT_LINE_COLOURS_KEY));
  const [recentFillColours, setRecentFillColours] = useState(getRecentColours(RECENT_FILL_COLOURS_KEY));
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [pathConfig, setPathConfig] = useState({
    progress: 0,
    speed: 1.5,
    autoMove: false,
    direction: 'forward',
  });
  const [showTokenIconModal, setShowTokenIconModal] = useState(false);

  const tokenIconUrl =
    shape && (shape.tokenIconUrl || shape.token_icon_url) || '';
  const mapIdForIcon =
    shape && (shape.mapId || shape.map_id) || null;
  const shapeIdForIcon =
    shape && (shape.id || shape.shapeId || shape.shape_id);

  useEffect(() => {
    setRecentLineColours(getRecentColours(RECENT_LINE_COLOURS_KEY));
    setRecentFillColours(getRecentColours(RECENT_FILL_COLOURS_KEY));
  }, []);

  useEffect(() => {
    if (!layer) return;
    if (layer.setStyle) {
      const styleObj = {
        color: shape.strokeColor || '#3388ff',
        fillColor: shape.fillColor || '#3388ff',
        opacity: shape.strokeOpacity ?? 1,
        fillOpacity: shape.fillOpacity ?? 0.5,
      };
      layer.setStyle(styleObj);
    }
    if (typeof window !== 'undefined' && window.L && layer instanceof window.L.Circle) {
      layer.setStyle({
        color: shape.strokeColor || '#3388ff',
        fillColor: shape.fillColor || '#3388ff',
        opacity: shape.strokeOpacity ?? 1,
        fillOpacity: shape.fillOpacity ?? 0.5,
      });
      if (shape.radius) layer.setRadius(shape.radius);
    }
  }, [
    shape.strokeColor,
    shape.fillColor,
    shape.strokeOpacity,
    shape.fillOpacity,
    shape.radius,
    layer,
  ]);

  const shapeType = layer ? getShapeType(layer) : 'polyline';
  const isClosedShape =
    shapeType === 'circle' ||
    shapeType === 'rectangle' ||
    shapeType === 'polygon';

  useEffect(() => {
    if (!shape) return;

    const bp = shape.behaviorProps
      || (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {})
      || {};

    const tokenDefaultPosition =
      typeof shape.tokenDefaultPosition === 'number'
        ? shape.tokenDefaultPosition
        : typeof bp.tokenDefaultPosition === 'number' ? bp.tokenDefaultPosition : 0;

    const tokenSpeedRaw =
      typeof shape.tokenSpeed === 'number'
        ? shape.tokenSpeed
        : typeof bp.tokenSpeed === 'number' ? bp.tokenSpeed : 1.5;

    const tokenAutoMove =
      typeof shape.tokenAutoMove === 'boolean'
        ? shape.tokenAutoMove
        : !!bp.tokenAutoMove;

    const tokenDirection =
      shape.tokenDirection ||
      bp.tokenDirection ||
      (isClosedShape ? 'clockwise' : 'forward');

    setPathConfig(prev => ({
      ...prev,
      progress: tokenDefaultPosition,
      speed: tokenSpeedRaw,
      autoMove: tokenAutoMove,
      direction: tokenDirection,
    }));
  }, [shape, isClosedShape]);

  useEffect(() => {
    if (!layer || !layer.pm) return;
    const st = getShapeType(layer);
    const allowSelfIntersection = st === 'polyline';

    if (canEdit && editing) {
      if (!layer.pm.enabled()) {
        layer.pm.enable({ allowSelfIntersection });
      } else if (typeof layer.pm.setOptions === 'function') {
        layer.pm.setOptions({ allowSelfIntersection });
      }
    } else {
      if (layer.pm && layer.pm.enabled()) {
        layer.pm.disable();
      }
    }
    return () => {
      if (layer && layer.pm && layer.pm.enabled()) {
        layer.pm.disable();
      }
    };
  }, [editing, layer, canEdit]);

  if (!shape || !canEdit) return null;

  function callOnChange(shapeUpdate) {
    if (convertToSnakeCase) {
      onChange(toSnakeCaseShape(shapeUpdate));
    } else {
      onChange(shapeUpdate);
    }
  }

  async function handleSaveToDB() {
    setSaveError('');
    setSaveBusy(true);
    try {
      const payload = serializeForBackend(shape);
      if (!payload.mapId) {
        throw new Error('mapId missing on shape; cannot save.');
      }
      const isUpdate = !!shape.id;
      const url = isUpdate ? `${API_BASE}/${shape.id}` : API_BASE;
      const method = isUpdate ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const saved = await res.json();
      if (onSave) onSave(saved);
      callOnChange({
        ...shape,
        id: saved.id ?? shape.id,
        strokeOpacity: saved.strokeOpacity ?? shape.strokeOpacity,
        fillOpacity: saved.fillOpacity ?? shape.fillOpacity,
        behaviorType: saved.behaviorType ?? payload.behaviorType ?? null,
        behaviorProps: saved.behaviorProps ?? payload.behaviorProps ?? {},
        behaviorState: saved.behaviorState ?? payload.behaviorState ?? {},
        sprite: saved.sprite ?? payload.sprite ?? null,
        tokenDefaultPosition: typeof saved.tokenDefaultPosition === 'number'
          ? saved.tokenDefaultPosition
          : shape.tokenDefaultPosition,
        tokenSpeed: typeof saved.tokenSpeed === 'number'
          ? saved.tokenSpeed
          : shape.tokenSpeed,
        tokenAutoMove: typeof saved.tokenAutoMove === 'boolean'
          ? saved.tokenAutoMove
          : shape.tokenAutoMove,
        tokenDirection: saved.tokenDirection || shape.tokenDirection,
        updatedAt: saved.updatedAt ?? new Date().toISOString(),
      });
      dispatchPolygonsChanged(saved.mapId ?? payload.mapId ?? shape.mapId ?? shape.map_id, saved.id ?? shape.id ?? null);
    } catch (e) {
      setSaveError(e?.message || 'Failed to save');
    } finally {
      setSaveBusy(false);
    }
  }

  function openLineColourModal() {
    setColourModal({
      type: 'line',
      original: shape.strokeColor || '#3388ff',
      value: shape.strokeColor || '#3388ff'
    });
  }

  function openFillColourModal() {
    setColourModal({
      type: 'fill',
      original: shape.fillColor || '#3388ff',
      value: shape.fillColor || '#3388ff'
    });
  }

  function handleColourChange(hex) {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      callOnChange({ ...shape, strokeColor: hex });
    } else {
      callOnChange({ ...shape, fillColor: hex });
    }
  }

  function handleColourCancel() {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      callOnChange({ ...shape, strokeColor: colourModal.original });
    } else {
      callOnChange({ ...shape, fillColor: colourModal.original });
    }
    setColourModal(null);
  }

  function handleColourConfirm(hex) {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      addRecentColour(RECENT_LINE_COLOURS_KEY, hex);
      setRecentLineColours(getRecentColours(RECENT_LINE_COLOURS_KEY));
      callOnChange({ ...shape, strokeColor: hex });
    } else {
      addRecentColour(RECENT_FILL_COLOURS_KEY, hex);
      setRecentFillColours(getRecentColours(RECENT_FILL_COLOURS_KEY));
      callOnChange({ ...shape, fillColor: hex });
    }
    setColourModal(null);
  }

  const behaviorTypeValue = shape.behaviorType ?? '';
  const spriteValue = shape.sprite ?? '';

  const bp = shape.behaviorProps
    || (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {})
    || {};

  const speechProps = getDefaultSpeechBehaviorProps(bp);
  const teleportDest = bp && bp.destinationMapId || '';

  const optionsList = [];
  const seen = new Set();

  if (parentMapId != null) {
    const pid = String(parentMapId);
    if (!seen.has(pid)) {
      optionsList.push({ id: parentMapId, name: parentMapName || 'Parent Map' });
      seen.add(pid);
    }
  }

  for (const m of Array.isArray(extraRegionMaps) ? extraRegionMaps : []) {
    if (!m || m.id == null) continue;
    const key = String(m.id);
    if (!seen.has(key)) {
      optionsList.push({ id: m.id, name: m.name || 'Map' });
      seen.add(key);
    }
  }

  function updateBehaviorProps(nextProps) {
    const existing = shape.behaviorProps
      || (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {})
      || {};
    const merged = { ...existing, ...nextProps };
    callOnChange({ ...shape, behaviorProps: merged });
  }

  const bs = shape.behaviorState
    || (shape.behavior_state ? tryParseJSON(shape.behavior_state, {}) : {})
    || {};

  const tokenIsLight = !!(bs.tokenIsLight || bs.light);

  function updateBehaviorState(nextState) {
    const existing = shape.behaviorState
      || (shape.behavior_state ? tryParseJSON(shape.behavior_state, {}) : {})
      || {};
    const merged = { ...existing, ...nextState };
    callOnChange({ ...shape, behaviorState: merged });
  }

  const isPathing = behaviorTypeValue === 'pathing';
  const isSpeechEffects = behaviorTypeValue === 'speech_effects';

  let currentLineMoveMode = 'pingpong';
  let currentLineDirection = 'forward';

  if (!isClosedShape && isPathing) {
    const dir = pathConfig.direction || 'forward';
    const isWrap = dir === 'wrap-forward' || dir === 'wrap-backward';
    currentLineMoveMode = isWrap ? 'wrap' : 'pingpong';
    const isBackward =
      dir === 'wrap-backward' ||
      dir === 'backward' ||
      dir === 'reverse';
    currentLineDirection = isBackward ? 'backward' : 'forward';
  }

  function pushPathConfigToBehavior(nextConfig) {
    const existing = shape.behaviorProps
      || (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {})
      || {};

    const speedMps = Math.max(0, Number(nextConfig.speed) || 0);

    const mergedProps = {
      ...existing,
      tokenDefaultPosition: nextConfig.progress,
      tokenSpeed: speedMps,
      tokenAutoMove: !!nextConfig.autoMove,
      tokenDirection: nextConfig.direction || (isClosedShape ? 'clockwise' : 'forward'),
    };

    callOnChange({
      ...shape,
      tokenDefaultPosition: nextConfig.progress,
      tokenSpeed: speedMps,
      tokenAutoMove: !!nextConfig.autoMove,
      tokenDirection: nextConfig.direction || (isClosedShape ? 'clockwise' : 'forward'),
      behaviorType: behaviorTypeValue || 'pathing',
      behaviorProps: mergedProps,
    });
  }

  function setBehaviorTypeValue(nextValue) {
    const existingProps = shape.behaviorProps
      || (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {})
      || {};
    const existingState = shape.behaviorState
      || (shape.behavior_state ? tryParseJSON(shape.behavior_state, {}) : {})
      || {};

    let nextProps = existingProps;

    if (nextValue === 'speech_effects') {
      nextProps = {
        ...existingProps,
        ...getDefaultSpeechBehaviorProps(existingProps),
      };
    }

    callOnChange({
      ...shape,
      behaviorType: nextValue,
      behaviorProps: nextProps,
      behaviorState: existingState,
    });
  }

  function applySpeechPreset(presetValue) {
    const preset = SPEECH_EFFECT_PRESETS.find(p => p.value === presetValue);
    if (!preset) return;
    updateBehaviorProps(preset.props);
  }

  return (
    <div style={{ ...toolbarStyle, height: 'auto', flexWrap: 'wrap', alignContent: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#fff', fontSize: 13 }}>Line</span>
        <span
          onClick={openLineColourModal}
          style={{
            display: 'inline-block',
            width: 26,
            height: 26,
            borderRadius: 7,
            background: shape.strokeColor || '#3388ff',
            border: '1.5px solid #ffe670',
            cursor: 'pointer',
            boxShadow: '0 1px 6px #000b'
          }}
          title="Line Colour"
        />
        <input
          type="range"
          min="0.05"
          max="1"
          step="0.01"
          value={shape.strokeOpacity ?? 1}
          onChange={e => callOnChange({ ...shape, strokeOpacity: parseFloat(e.target.value) })}
          style={{ width: 60, marginLeft: 5 }}
          title="Line Opacity"
        />
        <span style={{ color: '#ffe670', fontSize: 13, marginLeft: 4 }}>
          {Math.round((shape.strokeOpacity ?? 1) * 100)}%
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#fff', fontSize: 13 }}>Fill</span>
        <span
          onClick={openFillColourModal}
          style={{
            display: 'inline-block',
            width: 26,
            height: 26,
            borderRadius: 7,
            background: shape.fillColor || '#3388ff',
            border: '1.5px solid #ffe670',
            cursor: 'pointer',
            boxShadow: '0 1px 6px #000b'
          }}
          title="Fill Colour"
        />
        <input
          type="range"
          min="0.05"
          max="1"
          step="0.01"
          value={shape.fillOpacity ?? 0.5}
          onChange={e => callOnChange({ ...shape, fillOpacity: parseFloat(e.target.value) })}
          style={{ width: 60, marginLeft: 5 }}
          title="Fill Opacity"
        />
        <span style={{ color: '#ffe670', fontSize: 13, marginLeft: 4 }}>
          {Math.round((shape.fillOpacity ?? 0.5) * 100)}%
        </span>
      </div>

      <Panel label="Function">
        <select
          value={behaviorTypeValue}
          onChange={e => {
            const v = e.target.value || null;
            setBehaviorTypeValue(v);
          }}
          style={{
            background: '#16171b',
            color: '#ffe670',
            border: '1px solid #393b49',
            borderRadius: 6,
            padding: '6px 10px',
            fontWeight: 600,
            fontSize: 13,
            outline: 'none',
            cursor: 'pointer',
          }}
          title="Change Shape Function"
        >
          <option value="">Wall (default)</option>
          <option value="teleport">Teleport</option>
          <option value="pathing">Pathing</option>
          <option value="speech_effects">Speech Effect</option>
        </select>
      </Panel>

      <Panel label="Sprite">
        <input
          type="text"
          placeholder="https://.../sprite.gif"
          value={spriteValue}
          onChange={e => {
            const v = e.target.value;
            callOnChange({ ...shape, sprite: v || null });
          }}
          style={{
            width: 200,
            background: '#16171b',
            color: '#ffe670',
            border: '1px solid #393b49',
            borderRadius: 6,
            padding: '6px 10px',
            fontWeight: 600,
            fontSize: 13,
            outline: 'none',
          }}
          title="Sprite URL (optional)"
        />
      </Panel>

      {behaviorTypeValue === 'teleport' && (
        <Panel label="Destination Map">
          <select
            value={teleportDest}
            onChange={e => {
              updateBehaviorProps({
                destinationMapId: e.target.value || null,
              });
            }}
            style={{
              background: '#16171b',
              color: '#ffe670',
              border: '1px solid #393b49',
              borderRadius: 6,
              padding: '6px 10px',
              fontWeight: 600,
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">Select…</option>
            {optionsList.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </Panel>
      )}

      {isSpeechEffects && (
        <>
          <Panel label="Preset">
            <select
              defaultValue=""
              onChange={e => {
                const v = e.target.value;
                if (!v) return;
                applySpeechPreset(v);
                e.target.value = '';
              }}
              style={{
                background: '#16171b',
                color: '#ffe670',
                border: '1px solid #393b49',
                borderRadius: 6,
                padding: '6px 10px',
                fontWeight: 600,
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
              title="Apply a ready-made voice effect preset"
            >
              <option value="">Apply preset…</option>
              {SPEECH_EFFECT_PRESETS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Panel>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#fff', fontSize: 13 }}>Reverb</span>
            <input
              type="range"
              min="0"
              max="10"
              step="0.01"
              value={speechProps.reverbAmount}
              onChange={e => updateBehaviorProps({ reverbAmount: parseFloat(e.target.value) })}
              style={{ width: 90, marginLeft: 5 }}
              title="Reverb Amount"
            />
            <span
              style={{
                color: '#ffe670',
                fontSize: 13,
                marginLeft: 4,
                display: 'inline-block',
                minWidth: 40,
                textAlign: 'right',
              }}
            >
              {speechProps.reverbAmount.toFixed(2)}
            </span>
          </div>

          <Panel label="Character">
            <select
              value={speechProps.reverbCharacter}
              onChange={e => updateBehaviorProps({ reverbCharacter: e.target.value })}
              style={{
                background: '#16171b',
                color: '#ffe670',
                border: '1px solid #393b49',
                borderRadius: 6,
                padding: '6px 10px',
                fontWeight: 600,
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {REVERB_CHARACTERS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Panel>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#fff', fontSize: 13 }}>Compression</span>
            <input
              type="range"
              min="0"
              max="10"
              step="0.01"
              value={speechProps.compressionAmount}
              onChange={e => updateBehaviorProps({ compressionAmount: parseFloat(e.target.value) })}
              style={{ width: 90, marginLeft: 5 }}
              title="Compression Amount"
            />
            <span
              style={{
                color: '#ffe670',
                fontSize: 13,
                marginLeft: 4,
                display: 'inline-block',
                minWidth: 40,
                textAlign: 'right',
              }}
            >
              {speechProps.compressionAmount.toFixed(2)}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#fff', fontSize: 13 }}>Brightness</span>
            <input
              type="range"
              min="0"
              max="10"
              step="0.01"
              value={speechProps.brightnessAmount}
              onChange={e => updateBehaviorProps({ brightnessAmount: parseFloat(e.target.value) })}
              style={{ width: 90, marginLeft: 5 }}
              title="Brightness Amount"
            />
            <span
              style={{
                color: '#ffe670',
                fontSize: 13,
                marginLeft: 4,
                display: 'inline-block',
                minWidth: 40,
                textAlign: 'right',
              }}
            >
              {speechProps.brightnessAmount.toFixed(2)}
            </span>
          </div>
        </>
      )}

      {isPathing && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#fff', fontSize: 13 }}>Pos</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={pathConfig.progress}
              onChange={e => {
                const val = parseFloat(e.target.value);
                setPathConfig(prev => {
                  const next = { ...prev, progress: val };
                  pushPathConfigToBehavior(next);
                  return next;
                });
              }}
              style={{ width: 90, marginLeft: 5 }}
              title="Path Position"
            />
            <span
              style={{
                color: '#ffe670',
                fontSize: 13,
                marginLeft: 4,
                display: 'inline-block',
                minWidth: 40,
                textAlign: 'right',
              }}
            >
              {Math.round(pathConfig.progress * 100)}%
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#fff', fontSize: 13 }}>Speed</span>
            <input
              type="range"
              min="0"
              max="20"
              step="0.1"
              value={pathConfig.speed}
              onChange={e => {
                const raw = parseFloat(e.target.value);
                const clamped = Number.isFinite(raw) ? Math.max(0, raw) : 0;
                setPathConfig(prev => {
                  const next = { ...prev, speed: clamped };
                  pushPathConfigToBehavior(next);
                  return next;
                });
              }}
              style={{ width: 90, marginLeft: 5 }}
              title="Speed (m/s)"
            />
            <input
              type="number"
              min="0"
              max="20"
              step="0.1"
              value={pathConfig.speed}
              onChange={e => {
                const raw = parseFloat(e.target.value);
                const clamped = Number.isFinite(raw)
                  ? Math.max(0, raw)
                  : 0;
                setPathConfig(prev => {
                  const next = { ...prev, speed: clamped };
                  pushPathConfigToBehavior(next);
                  return next;
                });
              }}
              style={{
                width: 70,
                background: '#16171b',
                color: '#ffe670',
                border: '1px solid #393b49',
                borderRadius: 6,
                padding: '4px 6px',
                fontSize: 13,
                fontWeight: 600,
                outline: 'none',
                textAlign: 'right',
              }}
              title="Speed (metres per second)"
            />
            <span style={{ color: '#ffe670', fontSize: 13 }}>m/s</span>
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#fff',
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={pathConfig.autoMove}
              onChange={e => {
                const val = e.target.checked;
                setPathConfig(prev => {
                  const next = { ...prev, autoMove: val };
                  pushPathConfigToBehavior(next);
                  return next;
                });
              }}
            />
            Auto-move
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#fff',
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={tokenIsLight}
              onChange={e => {
                const val = e.target.checked;
                updateBehaviorState({
                  tokenIsLight: val,
                  light: val,
                });
              }}
            />
            Light source
          </label>

          {!isClosedShape && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#24253a',
                  border: '1px solid #393b49',
                  borderRadius: 7,
                  padding: '5px 9px',
                }}
              >
                <span style={{ color: '#fff', fontSize: 13 }}>
                  Line Move
                </span>
                <select
                  value={currentLineMoveMode}
                  onChange={e => {
                    const pattern = e.target.value;
                    setPathConfig(prev => {
                      const prevDir = prev.direction || 'forward';
                      const wasBackward =
                        prevDir === 'wrap-backward' ||
                        prevDir === 'backward' ||
                        prevDir === 'reverse';
                      const newDirection =
                        pattern === 'wrap'
                          ? wasBackward
                            ? 'wrap-backward'
                            : 'wrap-forward'
                          : wasBackward
                          ? 'backward'
                          : 'forward';
                      const next = {
                        ...prev,
                        direction: newDirection,
                      };
                      pushPathConfigToBehavior(next);
                      return next;
                    });
                  }}
                  style={{
                    background: '#16171b',
                    color: '#ffe670',
                    border: '1px solid #393b49',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 13,
                    fontWeight: 600,
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  title="How the token moves along the line"
                >
                  <option value="pingpong">Ping-pong</option>
                  <option value="wrap">Wrap (jump ends)</option>
                </select>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#24253a',
                  border: '1px solid #393b49',
                  borderRadius: 7,
                  padding: '5px 9px',
                }}
              >
                <span style={{ color: '#fff', fontSize: 13 }}>
                  Direction
                </span>
                <select
                  value={currentLineDirection}
                  onChange={e => {
                    const dirChoice = e.target.value;
                    setPathConfig(prev => {
                      const prevDir = prev.direction || 'forward';
                      const pattern =
                        prevDir === 'wrap-forward' ||
                        prevDir === 'wrap-backward'
                          ? 'wrap'
                          : 'pingpong';
                      const newDirection =
                        pattern === 'wrap'
                          ? dirChoice === 'backward'
                            ? 'wrap-backward'
                            : 'wrap-forward'
                          : dirChoice === 'backward'
                          ? 'backward'
                          : 'forward';
                      const next = {
                        ...prev,
                        direction: newDirection,
                      };
                      pushPathConfigToBehavior(next);
                      return next;
                    });
                  }}
                  style={{
                    background: '#16171b',
                    color: '#ffe670',
                    border: '1px solid #393b49',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 13,
                    fontWeight: 600,
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  title="Initial direction along the line"
                >
                  <option value="forward">Forward</option>
                  <option value="backward">Backward</option>
                </select>
              </div>
            </>
          )}

          {isClosedShape && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#24253a',
                border: '1px solid #393b49',
                borderRadius: 7,
                padding: '5px 9px',
              }}
            >
              <span style={{ color: '#fff', fontSize: 13 }}>
                Direction
              </span>
              <select
                value={pathConfig.direction || 'clockwise'}
                onChange={e => {
                  const val = e.target.value;
                  setPathConfig(prev => {
                    const next = { ...prev, direction: val };
                    pushPathConfigToBehavior(next);
                    return next;
                  });
                }}
                style={{
                  background: '#16171b',
                  color: '#ffe670',
                  border: '1px solid #393b49',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 13,
                  fontWeight: 600,
                  outline: 'none',
                  cursor: 'pointer',
                }}
                title="Path Direction"
              >
                <option value="clockwise">Clockwise</option>
                <option value="counterclockwise">
                  Counter-clockwise
                </option>
              </select>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: '#24253a',
              border: '1px solid #393b49',
              borderRadius: 7,
              padding: '5px 9px',
            }}
          >
            <span style={{ color: '#fff', fontSize: 13 }}>Token</span>
            {tokenIconUrl ? (
              <img
                src={tokenIconUrl}
                alt="Token Icon"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  border: '1px solid #000a',
                  background: '#000',
                  objectFit: 'contain',
                }}
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <span
                style={{
                  color: '#888',
                  fontSize: 12,
                  fontStyle: 'italic',
                }}
              >
                No icon
              </span>
            )}
            <button
              onClick={() => setShowTokenIconModal(true)}
              disabled={!mapIdForIcon || !shapeIdForIcon}
              title={
                !mapIdForIcon || !shapeIdForIcon
                  ? 'Save the shape first before adding an icon.'
                  : 'Change token icon'
              }
              style={{
                background:
                  !mapIdForIcon || !shapeIdForIcon ? '#333' : '#ffe670',
                color: '#23242a',
                border: 'none',
                borderRadius: 6,
                padding: '4px 10px',
                fontWeight: 600,
                fontSize: 13,
                cursor:
                  !mapIdForIcon || !shapeIdForIcon ? 'not-allowed' : 'pointer',
              }}
            >
              Change Icon
            </button>
          </div>
        </>
      )}

      <button
        onClick={() => setEditing(e => !e)}
        style={{
          background: editing ? '#ffe670' : '#393b49',
          color: editing ? '#23242a' : '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 17px',
          fontWeight: 700,
          fontSize: 16,
          marginLeft: 10,
          cursor: 'pointer',
        }}
        title={editing ? 'Finish Editing Shape' : 'Edit Shape'}
      >
        {editing ? 'Finish' : 'Edit'}
      </button>

      <button
        onClick={handleSaveToDB}
        disabled={saving || saveBusy}
        style={{
          background: '#4cc153',
          color: '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 18px',
          fontWeight: 700,
          fontSize: 16,
          cursor: (saving || saveBusy) ? 'not-allowed' : 'pointer',
          marginLeft: 10,
        }}
        title="Save to Database"
      >
        {(saving || saveBusy) ? 'Saving...' : 'Save'}
      </button>

      {saveError && (
        <span style={{ color: '#ff8c8c', fontSize: 12, marginLeft: 6 }}>
          {saveError}
        </span>
      )}

      <button
        onClick={() => setShowDeleteModal(true)}
        style={{
          background: '#e04c53',
          color: '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 18px',
          fontWeight: 700,
          fontSize: 16,
          marginLeft: 8,
          cursor: 'pointer',
        }}
        title="Delete"
      >
        Delete
      </button>

      <button
        onClick={onClose}
        style={{
          background: 'none',
          color: '#fff',
          border: 'none',
          fontSize: 20,
          marginLeft: 2,
          cursor: 'pointer',
          padding: 2,
          borderRadius: 6,
          lineHeight: 1,
        }}
        title="Close"
      >
        ×
      </button>

      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(24, 16, 20, 0.66)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#222124',
            padding: 38,
            borderRadius: 13,
            boxShadow: '0 2px 30px #000a',
            minWidth: 350,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24
          }}>
            <div style={{
              color: '#ffe670',
              fontWeight: 700,
              fontSize: 21,
              marginBottom: 8,
              textAlign: 'center'
            }}>
              Are you sure you want to delete this zone?
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: '#31313c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 23px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  dispatchPolygonsChanged(shape.mapId || shape.map_id || null, shape.id || null);
                  if (onDelete) onDelete(toSnakeCaseShape(shape), layer);
                }}
                style={{
                  background: '#e04c53',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 23px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showTokenIconModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(20,20,22,0.45)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ maxWidth: '90vw' }}>
            <IconUpload
              initialIconUrl={tokenIconUrl}
              mapId={mapIdForIcon}
              shapeId={shapeIdForIcon}
              onIconUploaded={url => {
                callOnChange({ ...shape, tokenIconUrl: url });
                setShowTokenIconModal(false);
              }}
              disabled={!mapIdForIcon || !shapeIdForIcon}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                onClick={() => setShowTokenIconModal(false)}
                style={{
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 17px',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {colourModal && (
        <ColourPickerModal
          value={colourModal.type === 'line' ? shape.strokeColor || '#3388ff' : shape.fillColor || '#3388ff'}
          recent={colourModal.type === 'line' ? recentLineColours : recentFillColours}
          onChange={handleColourChange}
          onCancel={handleColourCancel}
          onConfirm={handleColourConfirm}
          title={colourModal.type === 'line' ? 'Pick Line Colour' : 'Pick Fill Colour'}
        />
      )}
    </div>
  );
}